import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usuariosAPI, evaluacionesAPI, getImageUrl } from '../services/api';
import { useCerrarMenuAlClickFuera } from '../hooks/useCerrarMenuAlClickFuera';
import ResultadoPopup from './ResultadoPopup';
import './Evaluacion.css';

const competencias = [
  { key: 'innovacion', label: 'Innovación', code: 'In' },
  { key: 'empatia', label: 'Empatía', code: 'Em' },
  { key: 'inteligencia_emocional', label: 'Inteligencia Emocional', code: 'EQ' },
  { key: 'humor', label: 'Humor', code: 'H' },
  { key: 'negociacion', label: 'Negociación', code: 'N' },
  { key: 'motivacion', label: 'Motivación', code: 'M' },
  { key: 'proposito', label: 'Propósito', code: 'Pr' },
  { key: 'ego', label: 'Ego', code: 'Eg' },
  { key: 'trabajo_equipo', label: 'Trabajo en Equipo', code: 'T' },
  { key: 'adaptabilidad', label: 'Adaptabilidad', code: 'Adp' },
  { key: 'factor_contexto', label: 'Factor de Contexto', code: 'F' },
];

const Evaluacion = () => {
  const { usuarioId } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [usuario, setUsuario] = useState(null);
  const [pasoActual, setPasoActual] = useState(0);
  const [respuestas, setRespuestas] = useState({});
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [mostrarResultado, setMostrarResultado] = useState(false);
  const [resultadoEvaluacion, setResultadoEvaluacion] = useState(null);
  const [evaluacionId, setEvaluacionId] = useState(null);
  const [esEdicion, setEsEdicion] = useState(false);

  useCerrarMenuAlClickFuera(menuAbierto, setMenuAbierto);

  useEffect(() => {
    cargarDatos();
  }, [usuarioId]);

  const cargarDatos = async () => {
    await cargarUsuario();
    await verificarEvaluacionInconclusa();
  };

  const cargarUsuario = async () => {
    try {
      const respuesta = await usuariosAPI.obtenerPorId(usuarioId);
      setUsuario(respuesta.data);
    } catch (error) {
      console.error('Error al cargar usuario:', error);
    }
  };

  const verificarEvaluacionInconclusa = async () => {
    try {
      const respuesta = await usuariosAPI.obtenerEvaluacionInconclusa(usuarioId);
      if (respuesta.data.id) {
        const evaluacionData = respuesta.data;
        setEvaluacionId(evaluacionData.id);
        setEsEdicion(true);
        
        const respuestasGuardadas = {};
        competencias.forEach(comp => {
          if (evaluacionData[comp.key]) {
            respuestasGuardadas[comp.key] = evaluacionData[comp.key];
          }
        });
        setRespuestas(respuestasGuardadas);
        
        const ultimoIndice = competencias.findIndex(comp => !evaluacionData[comp.key]);
        if (ultimoIndice !== -1) {
          setPasoActual(ultimoIndice);
        } else {
          setPasoActual(competencias.length - 1);
        }
      }
    } catch (error) {
      console.error('Error al verificar evaluación inconclusa:', error);
    }
  };

  const competenciaActual = competencias[pasoActual];
  const progreso = Math.round(((pasoActual + 1) / competencias.length) * 100);

  const seleccionarCalificacion = async (valor) => {
    const nuevasRespuestas = {
      ...respuestas,
      [competenciaActual.key]: valor,
    };
    setRespuestas(nuevasRespuestas);
    
    await guardarProgreso(nuevasRespuestas);
  };

  const guardarProgreso = async (respuestasActuales) => {
    try {
      const datos = {
        usuario: usuarioId,
        completada: false,
        ...respuestasActuales
      };

      if (esEdicion && evaluacionId) {
        await evaluacionesAPI.actualizarParcial(evaluacionId, datos);
      } else {
        const respuesta = await evaluacionesAPI.crear(datos);
        setEvaluacionId(respuesta.data.id);
        setEsEdicion(true);
      }
    } catch (error) {
      console.error('Error al guardar progreso:', error);
    }
  };

  const siguiente = () => {
    if (pasoActual < competencias.length - 1) {
      setPasoActual(pasoActual + 1);
    } else {
      finalizarEvaluacion();
    }
  };

  const atras = () => {
    if (pasoActual > 0) {
      setPasoActual(pasoActual - 1);
    }
  };

  const getNivelLiderazgo = (puntuacion) => {
    if (puntuacion <= 20) return 'Liderazgo en desarrollo';
    if (puntuacion <= 40) return 'Liderazgo intermedio';
    if (puntuacion <= 50) return 'Liderazgo avanzado';
    if (puntuacion <= 80) return 'Liderazgo experto';
    return 'Liderazgo transformacional';
  };

  const finalizarEvaluacion = async () => {
    try {
      const datos = {
        usuario: usuarioId,
        completada: true,
        innovacion: respuestas.innovacion || 1,
        inteligencia_emocional: respuestas.inteligencia_emocional || 1,
        humor: respuestas.humor || 1,
        ego: respuestas.ego || 1,
        proposito: respuestas.proposito || 1,
        adaptabilidad: respuestas.adaptabilidad || 1,
        empatia: respuestas.empatia || 1,
        motivacion: respuestas.motivacion || 1,
        negociacion: respuestas.negociacion || 1,
        trabajo_equipo: respuestas.trabajo_equipo || 1,
        factor_contexto: respuestas.factor_contexto || 1,
      };

      let respuesta;
      if (esEdicion && evaluacionId) {
        respuesta = await evaluacionesAPI.actualizarParcial(evaluacionId, datos);
      } else {
        respuesta = await evaluacionesAPI.crear(datos);
      }
      
      const puntuacion = respuesta.data.puntuacion_total;
      
      setResultadoEvaluacion({
        usuario: usuario.nombre,
        puntuacion: puntuacion,
        nivelLiderazgo: getNivelLiderazgo(puntuacion)
      });
      setMostrarResultado(true);
    } catch (error) {
      console.error('Error al guardar evaluación:', error);
    }
  };

  const cerrarResultado = () => {
    setMostrarResultado(false);
    navigate('/');
  };

  const salir = () => {
    navigate('/');
  };

  return (
    <>
      <div className="evaluacion-container">
        <button className="menu-toggle" onClick={() => setMenuAbierto(!menuAbierto)}>
          ☰
        </button>

        <aside className={`eval-sidebar ${menuAbierto ? 'abierto' : ''}`}>
          <div className="user-profile">
            <div className="avatar-circle" style={{ backgroundImage: usuario?.imagen ? `url(${getImageUrl(usuario.imagen)})` : 'none' }}>
              {!usuario?.imagen && <span className="avatar-placeholder">📷</span>}
            </div>
            <h2 className="user-name">{usuario?.nombre || 'Usuario'}</h2>
            <div className="user-stats-divider"></div>
            <div className="user-stats">
              <p>Nivel de liderazgo: {usuario?.nivel_liderazgo || 'Sin evaluar'}</p>
              <p>Puntuación: {usuario?.puntuacion ? Math.round(usuario.puntuacion) : '0'}</p>
            </div>
          </div>

          <div className="sidebar-buttons">
            <button className="btn-sidebar active">
              Iniciar evaluación
            </button>
            <button className="btn-sidebar" onClick={() => navigate('/historial')}>
              Historial de evaluaciones
            </button>
          </div>

          <div className="logout-section">
            <div className="logout-text" onClick={() => {
              logout();
              navigate('/login');
            }}>
              Cerrar sesión
            </div>
            <button className="home-btn" onClick={() => navigate('/')} aria-label="Inicio">
              <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: '26px', height: '26px' }}>
                <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
              </svg>
            </button>
          </div>
        </aside>

        <main className="eval-main">
          <h1 className="eval-title">Evaluación de liderazgo</h1>
          
        <p className="eval-description">
          {competenciaActual.key === 'ego' ? (
            <>
              Evalúa la competencia "Ego - Eg" del 1 al 5, siendo 1 la calificación más baja y 5 la más alta.
              Esta variable representa un valor negativo. Cuanto más baja sea su calificación, mejor será para el resultado final.
            </>
          ) : competenciaActual.key === 'factor_contexto' ? (
            <>Evalúa el "Factor de Contexto - F" del 1 al 5</>
          ) : (
            <>Evalúa la competencia "{competenciaActual.label} - {competenciaActual.code}" del 1 al 5, siendo 1 la calificación más baja y 5 la más alta</>
          )}
        </p>

        <div className="progress-container">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${progreso}%` }}
            ></div>
            <p className="progress-text" style={{ color: progreso >= 55 ? '#e5e7eb' : '#3D3C43' }}>{progreso}%</p>
          </div>
        </div>

        {competenciaActual.key === 'factor_contexto' ? (
          <div className="rating-buttons-contexto">
            {[
              { valor: 1, label: 'Muy Favorable' },
              { valor: 2, label: 'Favorable' },
              { valor: 3, label: 'Neutro' },
              { valor: 4, label: 'Desfavorable' },
              { valor: 5, label: 'Muy Desfavorable' }
            ].map((opcion) => (
              <button
                key={opcion.valor}
                className={`rating-btn-contexto ${respuestas[competenciaActual.key] === opcion.valor ? 'selected' : ''}`}
                onClick={() => seleccionarCalificacion(opcion.valor)}
              >
                <span className="contexto-valor">{opcion.valor}</span>
                <span className="contexto-label">{opcion.label}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="rating-buttons">
            {[1, 2, 3, 4, 5].map((valor) => (
              <button
                key={valor}
                className={`rating-btn ${respuestas[competenciaActual.key] === valor ? 'selected' : ''}`}
                onClick={() => seleccionarCalificacion(valor)}
              >
                {valor}
              </button>
            ))}
          </div>
        )}

        <div className="nav-buttons">
          <button 
            className="btn-nav" 
            onClick={atras}
            disabled={pasoActual === 0}
          >
            Atrás
          </button>
          <button
            className="btn-nav"
            onClick={siguiente}
            disabled={!respuestas[competenciaActual.key]}
          >
            {pasoActual === competencias.length - 1 ? 'Finalizar' : 'Siguiente'}
          </button>
          <button className="btn-exit" onClick={salir}>
            Salir
          </button>
        </div>
        </main>
      </div>

      {mostrarResultado && resultadoEvaluacion && (
        <ResultadoPopup
          usuario={resultadoEvaluacion.usuario}
          puntuacion={resultadoEvaluacion.puntuacion}
          nivelLiderazgo={resultadoEvaluacion.nivelLiderazgo}
          onCerrar={cerrarResultado}
        />
      )}
    </>
  );
};

export default Evaluacion;

